import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, TrendingUp, DollarSign, Target, Sparkles,
  Shield, ArrowRight, BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

const PRODUCTION_BRACKETS = [
  "Under $2M / yr",
  "$2M – $5M / yr",
  "$5M – $10M / yr",
  "$10M – $20M / yr",
  "$20M+ / yr",
];

const benefits = [
  {
    icon: DollarSign,
    title: "Pay only when you produce.",
    body: "No upfront fees. No flat monthly retainer. You don't pay coaching dollars until your production crosses the agreed threshold — so the coaching has to actually work before it costs you anything.",
  },
  {
    icon: Target,
    title: "Built for producing agents.",
    body: "This isn't beginner content. Tammy works with experienced agents who already have a business — and want a thinking partner to scale it without burning it (or themselves) down.",
  },
  {
    icon: TrendingUp,
    title: "Real systems, not slogans.",
    body: "Listing presentations, lead-flow architecture, conversion review, mindset work, and the operational rituals that separate $5M producers from $20M producers.",
  },
  {
    icon: BadgeCheck,
    title: "Credentials that back it up.",
    body: "27-year Associate Broker. Master NLP Practitioner. KWRI MAPS Leadership Coach. Certified Mental Emotional Release & Executive Coach. The coaching is grounded in actual training — and decades of doing the work.",
  },
];

const howItWorks = [
  {
    n: "01",
    t: "Discovery call",
    d: "A free 30-minute conversation to see if we're a fit and to define what 'producing' means for your business.",
  },
  {
    n: "02",
    t: "Performance threshold set",
    d: "Together we agree on a clear production benchmark. Below the benchmark, you pay nothing for coaching. Above it, the fee kicks in.",
  },
  {
    n: "03",
    t: "Coaching begins",
    d: "Four coaching sessions per month, voice/text access between sessions, and full access to Tammy's playbooks and templates.",
  },
  {
    n: "04",
    t: "Quarterly review",
    d: "We measure honestly, recalibrate the plan, and reset the threshold up as your business grows.",
  },
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function ProducerCoaching() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", current_brokerage: "",
    experience_years: "", production: "", message: "",
  });
  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const fullMessage = form.production
        ? `Production: ${form.production}\n\n${form.message}`
        : form.message;
      await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        current_brokerage: form.current_brokerage,
        experience_years: form.experience_years,
        message: fullMessage,
        inquiry_type: "producer_coaching",
        subject: "Producer Coaching Inquiry",
      });
      toast.success("Application received! Tammy will reach out personally.");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="producer-coaching-success">
        <CheckCircle2 className="mx-auto text-primary" size={56} strokeWidth={1.4} />
        <h1 className="font-serif text-5xl mt-8">Thanks for reaching out.</h1>
        <p className="text-lg text-foreground/75 mt-6 leading-relaxed">
          Tammy will personally review your details and follow up by email
          within 2–3 business days to schedule a no-pressure discovery call.
        </p>
        <Link to="/" data-testid="producer-success-home">
          <Button variant="outline" className="mt-10 rounded-full px-8 h-12 border-border">
            Back to home
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="producer-coaching-page">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Agents reviewing strategy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 w-full">
          <span className="inline-block text-[11px] uppercase tracking-[0.32em] text-white/85 mb-6">
            For Producing Agents
          </span>
          <h1 className="font-serif text-white text-5xl md:text-6xl lg:text-7xl leading-[0.98] max-w-4xl">
            Coaching you only
            <br />
            <span className="italic text-primary">pay for when you win.</span>
          </h1>
          <p className="text-white/85 text-lg md:text-xl mt-8 max-w-2xl leading-relaxed">
            A performance-based coaching engagement for established agents.
            No retainers. No upfront fees. The coaching fee is only paid once
            your production crosses an agreed-on threshold.
          </p>
          <a href="#apply" data-testid="hero-apply-cta">
            <Button size="lg" className="mt-10 rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 px-8 text-base">
              Apply for a discovery call <ArrowRight className="ml-2" size={18} />
            </Button>
          </a>
        </div>
      </section>

      {/* Pitch / benefits */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-7">
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              The model is simple.
              <br />
              <span className="italic text-primary">The coaching has to work.</span>
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 text-foreground/75 leading-relaxed self-end text-lg">
            Most coaches get paid whether you grow or not. This engagement
            flips that — you pay <em>after</em> you've grown.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {benefits.map((b, i) => (
            <div
              key={b.title}
              className="bg-card border border-border rounded-2xl p-8 card-lift"
              data-testid={`producer-benefit-${i}`}
            >
              <b.icon className="text-primary" size={28} strokeWidth={1.5} />
              <h3 className="font-serif text-2xl mt-5">{b.title}</h3>
              <p className="text-sm text-foreground/75 mt-3 leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Risk-free strip */}
      <section className="bg-[#0F1E33] text-[#F5F8FB]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 text-center">
          <Shield className="text-[#93C5FD] mx-auto" size={32} strokeWidth={1.4} />
          <h2 className="font-serif text-4xl md:text-5xl mt-6 leading-tight">
            Skin in the game.
            <br />
            <span className="italic text-[#93C5FD]">Both ways.</span>
          </h2>
          <p className="text-lg mt-6 opacity-85 leading-relaxed max-w-2xl mx-auto">
            If you don't grow past the agreed threshold, you don't pay for
            coaching. Period. That's the only way a real performance partnership
            should work — and it keeps both of us honest.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="mb-16">
          <span className="text-[11px] uppercase tracking-[0.3em] text-primary">How it works</span>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight max-w-2xl">
            Four steps from <span className="italic text-primary">curious</span> to coaching.
          </h2>
        </div>
        <div className="grid md:grid-cols-4 gap-8">
          {howItWorks.map((s) => (
            <div key={s.n} className="border-t border-border pt-6">
              <p className="font-serif text-5xl text-primary">{s.n}</p>
              <p className="font-serif text-2xl mt-3">{s.t}</p>
              <p className="text-sm text-foreground/70 mt-3 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="bg-muted/40 py-24" data-testid="producer-apply-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
              <Sparkles size={14} /> Discovery Application
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              Apply for a
              <br />
              discovery call.
            </h2>
            <p className="mt-6 text-lg text-foreground/80 leading-relaxed">
              Fill this out and Tammy will personally review it. If it looks
              like a fit, she'll reach out by email within 2–3 business days
              to schedule a free 30-minute call. No pressure either way.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-foreground/80">
              {[
                "Goes directly to Tammy",
                "She reads every application herself",
                "Response within 2–3 business days",
              ].map((t) => (
                <li key={t} className="flex gap-3 items-start">
                  <CheckCircle2 className="text-primary shrink-0 mt-0.5" size={16} strokeWidth={1.6} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <form
            onSubmit={onSubmit}
            className="md:col-span-7 bg-card border border-border rounded-2xl p-8 md:p-10 space-y-5"
            data-testid="producer-coaching-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name *">
                <Input value={form.name} onChange={update("name")} className="rounded-xl h-11"
                  data-testid="producer-name" placeholder="Jane Cooper" required />
              </Field>
              <Field label="Email *">
                <Input type="email" value={form.email} onChange={update("email")} className="rounded-xl h-11"
                  data-testid="producer-email" placeholder="jane@example.com" required />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Phone">
                <Input value={form.phone} onChange={update("phone")} className="rounded-xl h-11"
                  data-testid="producer-phone" />
              </Field>
              <Field label="Years in real estate">
                <Input value={form.experience_years} onChange={update("experience_years")}
                  className="rounded-xl h-11" data-testid="producer-years" placeholder="e.g. 6" />
              </Field>
            </div>
            <Field label="Current brokerage">
              <Input value={form.current_brokerage} onChange={update("current_brokerage")}
                className="rounded-xl h-11" data-testid="producer-brokerage" placeholder="e.g. Compass · Boston" />
            </Field>
            <Field label="Approximate annual production">
              <Select value={form.production} onValueChange={update("production")}>
                <SelectTrigger className="rounded-xl h-11" data-testid="producer-production-trigger">
                  <SelectValue placeholder="Select a bracket" />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCTION_BRACKETS.map((b) => (
                    <SelectItem key={b} value={b}
                      data-testid={`producer-production-${b.replace(/[^a-zA-Z0-9]/g, '')}`}>
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="What's working — and what's stuck? *">
              <Textarea
                value={form.message}
                onChange={update("message")}
                rows={5}
                className="rounded-xl"
                data-testid="producer-message"
                placeholder="What does your business look like today? Where do you want to take it? What's getting in the way?"
                required
              />
            </Field>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="producer-submit"
              className="w-full rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 text-base"
            >
              {submitting ? "Sending..." : "Send my application"}
            </Button>
            <p className="text-[10px] text-center text-foreground/55 leading-relaxed">
              Your information goes only to Tammy and is never sold or shared.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70 font-medium">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
